import { inject, Injectable } from '@angular/core';
import {
  type DestroyOidcMutation,
  type DestroyOidcMutationVariables,
  type SharedViewerFragment,
  type SharedViewerQuery,
  type SharedViewerQueryVariables,
  type SharedViewerWithAvatarFragment,
  type SharedViewerWithAvatarQuery,
  type SharedViewerWithAvatarQueryVariables,
  type SharedViewerWithOidcQuery,
  type SharedViewerWithOidcQueryVariables,
  type ViewerIsOnboardedQuery,
  type ViewerIsOnboardedQueryVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SharedGraphQlService } from '../../services/shared-graphql.service';
import { destroyOidcMutation } from './shared-viewer.mutations';
import { sharedViewerQuery, sharedViewerWithAvatarQuery, sharedViewerWithOidcQuery } from './shared-viewer.queries';

export type SharedViewer = SharedViewerFragment;
export type SharedViewerWithAvatar = SharedViewerWithAvatarFragment;

@Injectable({ providedIn: 'root' })
export class SharedViewerApi {
  private sharedGraphQLService = inject(SharedGraphQlService);

  watchViewer() {
    return this.sharedGraphQLService
      .watchQuery<SharedViewerQuery, SharedViewerQueryVariables>({
        query: sharedViewerQuery,
      })
      .pipe(map((result) => result.data.viewer));
  }

  watchViewerWithAvatar() {
    return this.sharedGraphQLService
      .watchQuery<SharedViewerWithAvatarQuery, SharedViewerWithAvatarQueryVariables>({
        query: sharedViewerWithAvatarQuery,
      })
      .pipe(map((result) => result.data.viewer));
  }

  watchViewerOidc() {
    return this.sharedGraphQLService
      .watchQuery<SharedViewerWithOidcQuery, SharedViewerWithOidcQueryVariables>({
        query: sharedViewerWithOidcQuery,
      })
      .pipe(map((result) => result.data.viewer));
  }

  destroyOidc(id: string) {
    return this.sharedGraphQLService.mutateV2<DestroyOidcMutation, DestroyOidcMutationVariables>({
      mutation: destroyOidcMutation,
      variables: {
        input: {
          oidc: id,
        },
      },
      update: (cache) => {
        cache.evict({ id: 'OIDC:' + id });
        cache.gc();
      },
      refetchQueries: [{ query: sharedViewerWithOidcQuery }],
    });
  }

  watchViewerIsOnboarded(): Observable<boolean> {
    return (
      this.sharedGraphQLService
        .watchQuery<ViewerIsOnboardedQuery, ViewerIsOnboardedQueryVariables>({
          query: gql`
            query ViewerIsOnboarded {
              viewer {
                id
                isOnboarded
              }
            }
          `,
        })
        // @ts-expect-error TS18049 possibly null or undefined
        .pipe(map((result) => result.data.viewer.isOnboarded))
    );
  }
}
